import React from 'react';

import {Icons} from '../../assets';
import {CandidateProfile} from '../../models';
import {Button, ButtonSize, ButtonVariant} from '../button';
import {Card, CardPadding, CardRadius, CardVariant} from '../card';
import ProfilePicture from '../profile-picture';

interface CandidateCardProps {
  candidate: CandidateProfile;
  className?: string;
  disableBookmark?: boolean;
  isBookmarked?: boolean;
  isBookmarking?: boolean;
  onBookmarkToggle?: () => void;
  onViewAura?: () => void;
}

export const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  className = '',
  disableBookmark = false,
  isBookmarked = false,
  isBookmarking = false,
  onBookmarkToggle,
  onViewAura,
}) => {
  const headline = candidate.headline || 'Candidate';
  const email = candidate.email || 'Email unavailable';

  const handleBookmarkClick = () => {
    if (disableBookmark) {
      return;
    }
    onBookmarkToggle?.();
  };

  const handleViewAuraClick = () => {
    if (onViewAura) {
      onViewAura();
    }
  };

  return (
    <Card
      className={`relative flex w-full max-w-[300px] flex-col items-center gap-5 rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm transition hover:shadow-md md:max-w-[320px] ${className}`}
      padding={CardPadding.None}
      radius={CardRadius.None}
      variant={CardVariant.Flat}>
      <button
        aria-busy={isBookmarking}
        aria-label={isBookmarked ? 'Unsave candidate' : 'Save candidate'}
        aria-pressed={isBookmarked}
        className={`absolute right-5 top-5 rounded-full p-1 transition ${
          disableBookmark ? 'cursor-not-allowed opacity-40' : 'hover:scale-105'
        }`}
        disabled={disableBookmark || isBookmarking}
        onClick={handleBookmarkClick}
        type="button">
        <img alt="Bookmark" className="h-6 w-6" src={isBookmarked ? Icons.BOOKMARK_FILLED : Icons.BOOKMARK} />
      </button>

      <div className="flex flex-col items-center gap-3">
        <ProfilePicture name={candidate.fullName} url={candidate.profileImageUrl} />

        <div className="flex flex-col items-center gap-1">
          <p className="text-base font-semibold text-gray-900">{candidate.fullName}</p>
          <p className="text-sm font-medium text-gray-500">{headline}</p>

          <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
            <img alt="Email" className="h-4 w-4 opacity-60" src={Icons.EMAIL} />
            <span className="truncate font-medium">{email}</span>
          </div>
        </div>
      </div>

      <Button
        className="w-full rounded-full text-sm font-medium"
        fullWidth
        onClick={handleViewAuraClick}
        size={ButtonSize.MD}
        variant={ButtonVariant.Primary}>
        View Aura
      </Button>
    </Card>
  );
};
